import { action, computed, observable } from 'mobx'
import { type IValidationSchema, validationContext } from 'validx'
import type { LegendPartDependencies } from '@/features/legends/editor/legend-part-dependencies'
import { LegendPartViewModelBase } from '@/features/legends/editor/LegendPartViewModelBase'
import {
  addGenericErrorForProperty,
  type ErrorDescriptor,
  receiveErrors,
} from '@/features/legends/editor/analyzer-errors'
import { manageEach, moveItem } from '@/features/legends/editor/collections'
import type { RoutingPlan } from '@/features/legends/routing/routing'
import { FilterScopes } from '@/features/legends/filters/filter-scope'
import { validateElements } from '@/utils/validx-util'
import type { RoutingEditorViewModel } from './RoutingEditorViewModel'
import {
  RoutingPlanEditorViewModel,
  type RoutingPlanTab,
} from './RoutingPlanEditorViewModel'

const MAX_PLANS = 10

/**
 * The legend's routing plans, executed in order.
 */
export class RoutingPlansBuilderViewModel extends LegendPartViewModelBase {
  private readonly _routingEditor: RoutingEditorViewModel
  private readonly _schema: IValidationSchema<RoutingPlansBuilderViewModel> = {
    plans: [validateElements('There are invalid routing plans')],
  }

  readonly plans = observable.array<RoutingPlanEditorViewModel>([], {
    deep: false,
  })

  /**
   * The tab of the plan editor. It stays when another plan is selected.
   */
  @observable accessor currentTab: RoutingPlanTab = 'General'

  readonly validation = validationContext(this, this._schema)

  /**
   * @param dependencies
   * @param routingEditor The routing editor, which decides the symbols the
   * plans' requirements can reference and which plan the inspector edits.
   */
  constructor(
    dependencies: LegendPartDependencies,
    routingEditor: RoutingEditorViewModel,
  ) {
    super(dependencies)
    this._routingEditor = routingEditor
    this.lifecycle.manage(manageEach(this.lifecycle, () => this.plans))
  }

  /**
   * The plan the inspector edits.
   */
  @computed
  get selectedPlanVm() {
    const selected = this._routingEditor.selectedEditor
    return selected instanceof RoutingPlanEditorViewModel ? selected : null
  }

  /**
   * Whether another plan fits under the limit.
   */
  @computed
  get canAddPlan() {
    return this.plans.length < MAX_PLANS
  }

  /**
   * Adds an empty plan and selects it.
   */
  @action.bound
  add() {
    if (!this.canAddPlan) {
      return
    }
    const planVm = this._createPlan()
    this.plans.push(planVm)
    this.select(planVm)
  }

  /**
   * Opens the plan in the inspector.
   */
  @action.bound
  select(planVm: RoutingPlanEditorViewModel) {
    this._routingEditor.edit(planVm)
  }

  /**
   * Removes the plan, closing the inspector if it was editing it.
   */
  @action.bound
  remove(planVm: RoutingPlanEditorViewModel) {
    this.plans.remove(planVm)
    if (this.selectedPlanVm === planVm) {
      this._routingEditor.edit(null)
    }
  }

  /**
   * Moves a plan to another position.
   */
  @action.bound
  moveByIndex(fromIndex: number, toIndex: number) {
    moveItem(this.plans, fromIndex, toIndex)
  }

  /**
   * Switches the plan editor's tab.
   */
  @action.bound
  setTab(tab: RoutingPlanTab) {
    this.currentTab = tab
  }

  /**
   * Sets the plans from the legend document.
   */
  @action
  hydrate(plans: RoutingPlan[] | undefined) {
    this.plans.replace(
      (plans ?? []).map((data) => {
        const planVm = this._createPlan()
        planVm.hydrate(data)
        return planVm
      }),
    )
  }

  /**
   * Shows the analyzer's errors on the plans.
   */
  receiveErrors(descriptor: ErrorDescriptor) {
    addGenericErrorForProperty(
      this,
      descriptor,
      'plans',
      'There are invalid plans',
    )
    receiveErrors(this.plans, descriptor.inner)
  }

  /**
   * The plans as the legend document stores them.
   */
  toJSON(): RoutingPlan[] {
    return this.plans.map((planVm) => planVm.toJSON())
  }

  private _createPlan() {
    return new RoutingPlanEditorViewModel(
      this.dependencies,
      FilterScopes.ALL,
      this._routingEditor,
    )
  }
}
